import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMyContext } from '../../../../context/MyContext';
import Sidebar from '../../../../component/b2b/sideBar/b2bSideBar';
import NavBar from '../../../../component/b2b/navBar/b2bNavBar';
import '../../../../css/custom-scrollbar.css';
import { Base_Url, b2bRateCard } from '../../../../config/config';
import { FcMoneyTransfer } from "react-icons/fc";
import { toast } from 'react-toastify';
import { Package, MapPin, ChevronDown, Layers, Receipt, IndianRupee, Check, Search, X, Truck, Clock, Shield, FileText } from 'lucide-react';

const B2BRateCard = () => {
  const [zoneMetrics, setZoneMetrics] = useState({});
  const [showZoneInfo, setShowZoneInfo] = useState({});
  const { isDarkMode, isToggleSidebar } = useMyContext();
  const [data, setData] = useState([]);
  const [showVasCharges, setShowVasCharges] = useState({});
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');
  const cus_id = localStorage.getItem('cus_id');

  const zoneMapping = {
    'n1': 'N1', 'n2': 'N2', 'n3': 'N3', 'n4': 'N4',
    'c1': 'C1', 'c2': 'C2',
    'w1': 'W1', 'w2': 'W2',
    's1': 'S1', 's2': 'S2', 's3': 'S3', 's4': 'S4',
    'e1': 'E1', 'e2': 'E2',
    'ne1': 'NE1', 'ne2': 'NE2'
  };

  useEffect(() => {
    fetchRateCardList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRateCardList = async () => {
    try {
      setLoading(true);
      const url = Base_Url + b2bRateCard;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          token: token || '',
          cus_id: cus_id || '',
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();

      if (result.status === "SUCCESS") {
        setData(result.data || []);
        setZoneMetrics(result.zonemetrics || {});
        if (result.data && result.data.length > 0) {
          const firstId = result.data[0].id !== undefined && result.data[0].id !== null ? result.data[0].id : 0;
          setSelectedCardId(firstId);
          setActivePartner(result.data[0].partner);
        }
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Error fetching rate card data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const [selectedCardId, setSelectedCardId] = useState(null);
  const [activePartner, setActivePartner] = useState(null);

  const parsePricingData = (pricingString) => {
    try {
      if (!pricingString) return {};
      const parsed = typeof pricingString === 'string' ? JSON.parse(pricingString.trim()) : pricingString;
      return (parsed && typeof parsed === 'object') ? parsed : {};
    } catch (error) {
      console.error('Error parsing pricing data:', error);
      return {};
    }
  };

  const parseVasData = (vasString) => {
    try {
      if (!vasString) return [];
      const parsed = typeof vasString === 'string' ? JSON.parse(vasString.trim()) : vasString;
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(charge => charge && typeof charge === 'object');
    } catch (error) {
      console.error('Error parsing VAS data:', error);
      return [];
    }
  };

  const getZonePricing = (pricingData, fromZone, toZone) => {
    if (!pricingData || typeof pricingData !== 'object') {
      return '-';
    }
    if (!pricingData[fromZone] || !pricingData[fromZone][toZone]) {
      return '-';
    }
    return pricingData[fromZone][toZone];
  };

  const formatChargeValue = (charge) => {
    if (!charge) return '-';
    if (charge.charge_type === 'percent') {
      return `${charge.value}%`;
    } else if (charge.charge_type === 'per_kg') {
      return `₹${charge.value}/kg`;
    } else {
      return `₹${charge.value}`;
    }
  };

  const [vasSearch, setVasSearch] = useState('');
  const [vasTypeFilter, setVasTypeFilter] = useState('ALL');

  const getVasIcon = (chargeKey = '', label = '') => {
    const text = `${chargeKey} ${label}`.toLowerCase();
    if (text.includes('demurrage') || text.includes('holding') || text.includes('detention') || text.includes('time') || text.includes('delay')) {
      return <Clock size={16} className="text-amber-500" />;
    }
    if (text.includes('oda') || text.includes('delivery') || text.includes('attempt') || text.includes('re-attempt') || text.includes('truck') || text.includes('csd')) {
      return <Truck size={16} className="text-blue-500" />;
    }
    if (text.includes('insur') || text.includes('fov') || text.includes('risk') || text.includes('shield') || text.includes('security')) {
      return <Shield size={16} className="text-emerald-500" />;
    }
    if (text.includes('pod') || text.includes('doc') || text.includes('bill') || text.includes('waybill') || text.includes('invoice')) {
      return <FileText size={16} className="text-purple-500" />;
    }
    return <Receipt size={16} className="text-indigo-500" />;
  };

  const formatChargeLabel = (charge) => {
    if (charge.label && charge.label.trim()) return charge.label;
    if (charge.charge_key) {
      return charge.charge_key
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }
    return 'Value Added Service';
  };

  const formatChargeType = (type) => {
    const t = String(type || '').toLowerCase();
    if (t === 'fixed') return 'Fixed Fee';
    if (t === 'per_kg') return 'Per Kg Rate';
    if (t === 'percent') return 'Percentage Rate';
    return type || 'Standard Charge';
  };

  const formatConditionValue = (cond) => {
    if (!cond || cond === '0' || cond === 0) return null;
    const c = String(cond).toLowerCase();
    if (c === 'lr') return 'Per LR (Consignment)';
    if (c === 'weight') return 'Based on Weight';
    return String(cond);
  };

  const [matrixOrigin, setMatrixOrigin] = useState('');
  const [matrixDest, setMatrixDest] = useState('');
  const [matrixOriginRegion, setMatrixOriginRegion] = useState('ALL');

  const [partnerFilter, setPartnerFilter] = useState('ALL');

  const uniquePartners = useMemo(() => {
    const map = new Map();
    (data || []).forEach(item => {
      const name = item.partner || '';
      const key = name.toLowerCase().trim();
      if (!key) return;
      if (!map.has(key)) {
        map.set(key, { partner: name, count: 1 });
      } else {
        map.get(key).count += 1;
      }
    });
    return Array.from(map.values());
  }, [data]);

  const visibleCards = useMemo(() => {
    if (partnerFilter === 'ALL') return data || [];
    return (data || []).filter(
      item => (item.partner || '').toLowerCase().trim() === partnerFilter.toLowerCase().trim()
    );
  }, [data, partnerFilter]);

  const handleFilterPartner = (partnerName) => {
    setPartnerFilter(partnerName);
    if (partnerName !== 'ALL') {
      const match = (data || []).find(
        item => (item.partner || '').toLowerCase().trim() === partnerName.toLowerCase().trim()
      );
      if (match) {
        const originalIndex = data.indexOf(match);
        const cardId = match.id !== undefined && match.id !== null ? match.id : originalIndex;
        setSelectedCardId(cardId);
        setActivePartner(match.partner);
      }
    }
  };

  const getPartnerLogo = (partner) => {
    if (!partner) return '/assets/images/logo1.png';
    const normalized = String(partner).toLowerCase().replace(/[\s_-]+/g, '');

    if (normalized.includes('delhivery')) return '/assets/images/Delhivery.png';
    if (normalized.includes('ekart')) return '/assets/images/ekart.png';
    if (normalized.includes('gati')) return '/assets/images/Gati.jpg';
    if (normalized.includes('movin') || normalized.includes('movein')) return '/assets/images/movin.svg';
    if (normalized.includes('pickup') || normalized.includes('px')) return '/assets/images/logo1.png';
    if (normalized.includes('blue') || normalized.includes('dart')) return '/assets/images/bluedart.png';
    if (normalized.includes('xpress') || normalized.includes('xb')) return '/assets/images/xpressbeeLogo.png';
    if (normalized.includes('ecom')) return '/assets/images/ecom.png';
    if (normalized.includes('safe') || normalized.includes('sfx')) return '/assets/images/safexpress.png';

    return '/assets/images/logo1.png';
  };

  const toggleVasCharges = (partner) => {
    setShowVasCharges(prev => ({
      ...prev,
      [partner]: !prev[partner]
    }));
  };

  const toggleZoneInfo = () => {
    setShowZoneInfo(prev => ({
      ...prev,
      [activePartner]: !prev[activePartner]
    }));
  };

  const [zoneSearch, setZoneSearch] = useState('');
  const [selectedZoneRegion, setSelectedZoneRegion] = useState('ALL');

  const getZoneColor = (zone) => {
    const colors = {
      'N1': 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30',
      'N2': 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30',
      'N3': 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:border-indigo-500/30',
      'N4': 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:border-indigo-500/30',
      'C1': 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-500/15 dark:text-violet-300 dark:border-violet-500/30',
      'C2': 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-500/15 dark:text-violet-300 dark:border-violet-500/30',
      'W1': 'bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-500/15 dark:text-pink-300 dark:border-pink-500/30',
      'W2': 'bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-500/15 dark:text-pink-300 dark:border-pink-500/30',
      'S1': 'bg-red-50 text-red-700 border-red-200 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/30',
      'S2': 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-500/15 dark:text-orange-300 dark:border-orange-500/30',
      'S3': 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30',
      'S4': 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-500/15 dark:text-yellow-300 dark:border-yellow-500/30',
      'E1': 'bg-green-50 text-green-700 border-green-200 dark:bg-green-500/15 dark:text-green-300 dark:border-green-500/30',
      'E2': 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30',
      'NE1': 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-500/15 dark:text-teal-300 dark:border-teal-500/30',
      'NE2': 'bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-500/15 dark:text-cyan-300 dark:border-cyan-500/30'
    };
    return colors[zone] || 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-white/10 dark:text-gray-300 dark:border-white/20';
  };

  const getZoneRegionInfo = (zoneCode) => {
    const z = String(zoneCode || '').toUpperCase().trim();
    if (z.startsWith('NE')) return { region: 'North-East', key: 'NE', color: 'teal', dot: 'bg-teal-500' };
    if (z.startsWith('N')) return { region: 'North', key: 'N', color: 'blue', dot: 'bg-blue-500' };
    if (z.startsWith('C')) return { region: 'Central', key: 'C', color: 'violet', dot: 'bg-violet-500' };
    if (z.startsWith('W')) return { region: 'West', key: 'W', color: 'pink', dot: 'bg-pink-500' };
    if (z.startsWith('S')) return { region: 'South', key: 'S', color: 'orange', dot: 'bg-orange-500' };
    if (z.startsWith('E')) return { region: 'East', key: 'E', color: 'green', dot: 'bg-emerald-500' };
    return { region: 'Other', key: 'OTHER', color: 'gray', dot: 'bg-gray-400' };
  };

  const zoneRegionTabs = [
    { key: 'ALL', label: 'All Regions' },
    { key: 'N', label: 'North', dot: 'bg-blue-500' },
    { key: 'C', label: 'Central', dot: 'bg-violet-500' },
    { key: 'W', label: 'West', dot: 'bg-pink-500' },
    { key: 'S', label: 'South', dot: 'bg-orange-500' },
    { key: 'E', label: 'East', dot: 'bg-emerald-500' },
    { key: 'NE', label: 'North-East', dot: 'bg-teal-500' },
  ];

  const regionGroups = [
    { title: 'North', zones: ['n1', 'n2', 'n3', 'n4'], color: 'blue' },
    { title: 'Central', zones: ['c1', 'c2'], color: 'violet' },
    { title: 'West', zones: ['w1', 'w2'], color: 'pink' },
    { title: 'South', zones: ['s1', 's2', 's3', 's4'], color: 'orange' },
    { title: 'East', zones: ['e1', 'e2'], color: 'green' },
    { title: 'Northeast', zones: ['ne1', 'ne2'], color: 'teal' },
  ];

  const totalZoneAreasCount = useMemo(() => {
    return Object.values(zoneMetrics || {}).reduce((acc, locs) => acc + (Array.isArray(locs) ? locs.length : 0), 0);
  }, [zoneMetrics]);

  const getRegionCount = (regionKey) => {
    const entries = Object.entries(zoneMetrics || {});
    if (regionKey === 'ALL') return entries.length;
    return entries.filter(([z]) => getZoneRegionInfo(z).key === regionKey).length;
  };

  const filteredZoneEntries = useMemo(() => {
    const entries = Object.entries(zoneMetrics || {});
    return entries.filter(([zone, locations]) => {
      const regionInfo = getZoneRegionInfo(zone);
      const matchesRegion = selectedZoneRegion === 'ALL' || regionInfo.key === selectedZoneRegion;
      if (!matchesRegion) return false;

      if (!zoneSearch.trim()) return true;
      const q = zoneSearch.toLowerCase().trim();
      const zoneMatches = zone.toLowerCase().includes(q) || regionInfo.region.toLowerCase().includes(q);
      const locationMatches = Array.isArray(locations) && locations.some(loc => String(loc).toLowerCase().includes(q));
      return zoneMatches || locationMatches;
    });
  }, [zoneMetrics, selectedZoneRegion, zoneSearch]);

  const glass =
    "backdrop-blur-2xl backdrop-saturate-150 bg-white/50 dark:bg-white/[0.06] " +
    "border border-white/60 dark:border-white/10 " +
    "shadow-[0_8px_32px_rgba(31,41,55,0.08)]";

  return (
    <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
      <div className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-indigo-200/30 dark:bg-indigo-500/10 blur-3xl" />

      <NavBar />

      <div className="flex flex-1 overflow-hidden">
        <div
          className={`${isToggleSidebar ? "md:w-64" : "md:w-20"} w-0 m-0 md:m-3 md:rounded-3xl ${glass} overflow-visible md:overflow-hidden transition-all duration-300 ease-in-out flex-shrink-0`}
        >
          <Sidebar />
        </div>

        <div className={`w-full flex flex-1 flex-col custom-scrollbar overflow-y-auto ${isDarkMode ? 'dark:bg-darkmodeColor text-white' : 'bg-gray-100'}`}>
          <div className="p-3 sm:p-4 max-w-[1500px] mx-auto w-full">

            {/* ===== HEADER ===== */}
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mb-4"
            >
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-gradient-to-br from-indigo-600 to-purple-600 shadow-lg shadow-indigo-900/50' : 'bg-gradient-to-br from-red-500 to-red-600 shadow-md shadow-red-200'}`}>
                  <FcMoneyTransfer size={20} className="text-white" />
                </div>
                <div className="min-w-0">
                  <h1 className={`text-lg sm:text-2xl font-bold leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Rate Card Dashboard
                  </h1>
                  <p className={`text-xs sm:text-sm ${isDarkMode ? 'text-indigo-300/70' : 'text-gray-500'}`}>
                    Zone pricing, VAS charges and courier partner rates
                  </p>
                </div>
              </div>
            </motion.div>

            {loading ? (
              <div className={`flex flex-col items-center justify-center h-64 rounded-2xl border ${isDarkMode ? 'border-indigo-500/25 dark:bg-darkmodeColor' : 'border-gray-200 bg-white'}`}>
                <div className="relative h-12 w-12">
                  <div className={`absolute inset-0 rounded-full border-4 ${isDarkMode ? 'border-indigo-500/20' : 'border-gray-100'}`}></div>
                  <div className={`absolute inset-0 rounded-full border-4 border-transparent animate-spin ${isDarkMode ? 'border-t-indigo-400' : 'border-t-red-500'}`}></div>
                </div>
                <p className={`mt-4 text-sm font-medium ${isDarkMode ? 'text-indigo-300/70' : 'text-gray-500'}`}>Fetching rate cards...</p>
              </div>
            ) : data.length === 0 ? (
              <div className={`rounded-2xl p-8 sm:p-12 text-center border ${isDarkMode ? 'border-indigo-500/25 dark:bg-darkmodeColor' : 'border-gray-200 bg-white'}`}>
                <div className={`mx-auto mb-4 w-16 h-16 rounded-2xl flex items-center justify-center ${isDarkMode ? 'bg-indigo-500/10' : 'bg-gray-100'}`}>
                  <Package className={isDarkMode ? 'text-indigo-400/50' : 'text-gray-400'} size={28} />
                </div>
                <h3 className={`text-base font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>No rate cards available</h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  There are no courier partner rate cards to display at this time.
                </p>
              </div>
            ) : (
              <>
                {/* ===== COURIER PARTNER SELECTION & FILTER ===== */}
                <div className="mb-4">
                  {/* Top Bar: Title + Quick Partner Filter */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 mb-2.5">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-indigo-300/80' : 'text-gray-600'}`}>
                        Courier Partner & Rates
                      </span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                        isDarkMode ? 'bg-indigo-500/20 text-indigo-300' : 'bg-gray-200 text-gray-700'
                      }`}>
                        {data.length} Available
                      </span>
                    </div>

                    {/* Filter Pills */}
                    {uniquePartners.length > 1 && (
                      <div className="flex items-center gap-1.5 overflow-x-auto custom-scrollbar pb-1 sm:pb-0">
                        <button
                          type="button"
                          onClick={() => setPartnerFilter('ALL')}
                          className={`px-3 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-150 ${
                            partnerFilter === 'ALL'
                              ? isDarkMode
                                ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-900/50 ring-1 ring-indigo-400'
                                : 'bg-red-500 text-white shadow-sm shadow-red-200'
                              : isDarkMode
                                ? 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10'
                                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          All ({data.length})
                        </button>

                        {uniquePartners.map(({ partner, count }) => {
                          const isFilterActive = partnerFilter.toLowerCase().trim() === partner.toLowerCase().trim();
                          return (
                            <button
                              key={partner}
                              type="button"
                              onClick={() => handleFilterPartner(partner)}
                              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-150 ${
                                isFilterActive
                                  ? isDarkMode
                                    ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-900/50 ring-1 ring-indigo-400'
                                    : 'bg-red-500 text-white shadow-sm shadow-red-200'
                                  : isDarkMode
                                    ? 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10'
                                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              <img
                                src={getPartnerLogo(partner)}
                                alt=""
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = '/assets/images/logo1.png';
                                }}
                                className="w-3.5 h-3.5 object-contain rounded bg-white p-0.5"
                              />
                              <span className="capitalize">{partner}</span>
                              <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                                isFilterActive
                                  ? 'bg-white/20 text-white'
                                  : isDarkMode ? 'bg-white/10 text-gray-300' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {count}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Compact Rate Cards Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2.5">
                    {visibleCards.map((item, index) => {
                      const originalIndex = data.indexOf(item);
                      const cardId = item.id !== undefined && item.id !== null ? item.id : (originalIndex !== -1 ? originalIndex : index);
                      const isSelected = selectedCardId !== null
                        ? selectedCardId === cardId
                        : (activePartner === item.partner && (originalIndex === 0 || originalIndex === -1));
                      const cftLabel = item.cft_type || item.type || item.cft || `CFT-${(originalIndex !== -1 ? originalIndex : index) + 1}`;
                      const pPricing = parsePricingData(item.zone_pricing);
                      const zonesCount = Object.keys(pPricing).length;

                      return (
                        <button
                          key={cardId}
                          type="button"
                          onClick={() => {
                            setSelectedCardId(cardId);
                            setActivePartner(item.partner);
                          }}
                          className={`group relative flex items-center justify-between p-2.5 rounded-xl border text-left transition-all duration-150 ${
                            isSelected
                              ? isDarkMode
                                ? 'bg-indigo-600/20 border-indigo-400 text-white shadow-md shadow-indigo-950/40 ring-1 ring-indigo-400/40'
                                : 'bg-red-50/80 border-red-500 text-gray-900 shadow-sm ring-1 ring-red-500/30'
                              : isDarkMode
                                ? 'bg-white/[0.04] border-white/10 hover:bg-white/[0.08] hover:border-white/20 text-gray-200'
                                : 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50/80 text-gray-800'
                          }`}
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className={`w-9 h-9 rounded-lg p-1 flex items-center justify-center shrink-0 border ${
                              isSelected
                                ? 'bg-white border-white/40 shadow-xs'
                                : isDarkMode ? 'bg-white/10 border-white/10' : 'bg-gray-50 border-gray-200'
                            }`}>
                              <img
                                src={getPartnerLogo(item.partner)}
                                alt={`${item.partner} logo`}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = '/assets/images/logo1.png';
                                }}
                                className="w-full h-full object-contain"
                              />
                            </div>

                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className={`text-xs sm:text-sm font-bold capitalize truncate ${
                                  isSelected ? (isDarkMode ? 'text-white' : 'text-gray-900') : (isDarkMode ? 'text-gray-100' : 'text-gray-800')
                                }`}>
                                  {item.partner}
                                </span>
                                {isSelected && (
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0 animate-pulse" />
                                )}
                              </div>

                              <div className="flex items-center gap-1.5 mt-0.5">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
                                  isSelected
                                    ? isDarkMode
                                      ? 'bg-indigo-500/30 text-indigo-200 border border-indigo-400/40'
                                      : 'bg-red-100 text-red-700 border border-red-200'
                                    : isDarkMode
                                      ? 'bg-white/10 text-gray-300'
                                      : 'bg-gray-100 text-gray-600'
                                }`}>
                                  CFT: {cftLabel}
                                </span>
                                <span className={`text-[10px] font-medium ${
                                  isSelected ? (isDarkMode ? 'text-indigo-200' : 'text-gray-600') : (isDarkMode ? 'text-gray-400' : 'text-gray-500')
                                }`}>
                                  • {zonesCount} zones
                                </span>
                              </div>
                            </div>
                          </div>

                          {isSelected && (
                            <div className={`shrink-0 ml-2 px-1.5 py-0.5 rounded-md text-[10px] font-bold flex items-center gap-1 ${
                              isDarkMode ? 'bg-indigo-500/30 text-indigo-200 border border-indigo-400/30' : 'bg-red-500 text-white'
                            }`}>
                              <Check size={11} strokeWidth={3} />
                              <span className="hidden sm:inline">Active</span>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* ===== ZONE INFO SECTION ===== */}
                {Object.keys(zoneMetrics).length > 0 && (
                  <div className={`rounded-2xl overflow-hidden mb-4 border transition-all duration-200 ${
                    isDarkMode ? 'border-indigo-500/25 bg-darkmodeColor shadow-lg shadow-black/20' : 'border-gray-200 bg-white shadow-sm'
                  }`}>
                    {/* Accordion Header */}
                    <button
                      type="button"
                      onClick={toggleZoneInfo}
                      className={`w-full px-4 py-3 sm:py-3.5 flex items-center justify-between gap-3 text-left transition-all ${
                        isDarkMode
                          ? 'bg-indigo-500/10 text-white hover:bg-indigo-500/15'
                          : 'bg-gradient-to-r from-gray-50 via-white to-gray-50 text-gray-900 hover:bg-gray-100/60'
                      }`}
                    >
                      <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          isDarkMode ? 'bg-indigo-500/20 text-indigo-300' : 'bg-red-50 text-red-600 border border-red-100'
                        }`}>
                          <MapPin size={16} />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-sm sm:text-base">
                              Zone Information & Coverage
                            </span>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                              isDarkMode ? 'bg-indigo-500/30 text-indigo-200 border border-indigo-400/30' : 'bg-red-50 text-red-700 border border-red-200'
                            }`}>
                              {Object.keys(zoneMetrics).length} Zones
                            </span>
                            <span className={`hidden sm:inline-block text-[10px] px-2 py-0.5 rounded-full font-medium ${
                              isDarkMode ? 'bg-white/5 text-gray-300' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {totalZoneAreasCount} Areas Covered
                            </span>
                          </div>
                          <p className={`text-[11px] truncate mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Coverage mapping across North, South, East, West, Central & North-East zones
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`text-xs font-semibold hidden md:inline ${
                          isDarkMode ? 'text-indigo-300/70' : 'text-gray-500'
                        }`}>
                          {showZoneInfo[activePartner] ? 'Hide Details' : 'View Coverage'}
                        </span>
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                          isDarkMode ? 'bg-white/5 text-indigo-300' : 'bg-gray-100 text-gray-600'
                        }`}>
                          <ChevronDown
                            className={`w-4 h-4 transition-transform duration-300 ${
                              showZoneInfo[activePartner] ? 'rotate-180' : ''
                            }`}
                          />
                        </div>
                      </div>
                    </button>

                    {/* Accordion Content */}
                    <AnimatePresence initial={false}>
                      {showZoneInfo[activePartner] && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className={`${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'} overflow-hidden border-t ${
                            isDarkMode ? 'border-indigo-500/20' : 'border-gray-100'
                          }`}
                        >
                          <div className="p-3 sm:p-4 space-y-3">
                            {/* Toolbar: Search input + Region Tabs */}
                            <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-2.5 pb-2 border-b border-gray-100 dark:border-white/5">
                              {/* Search Input */}
                              <div className="relative flex-1 max-w-md">
                                <Search
                                  size={15}
                                  className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                                    isDarkMode ? 'text-gray-400' : 'text-gray-400'
                                  }`}
                                />
                                <input
                                  type="text"
                                  value={zoneSearch}
                                  onChange={(e) => setZoneSearch(e.target.value)}
                                  placeholder="Search city, state or zone (e.g. Jaipur, N1, Mumbai)..."
                                  className={`w-full pl-9 pr-8 py-1.5 rounded-lg text-xs outline-none transition-all ${
                                    isDarkMode
                                      ? 'bg-white/5 border border-white/10 text-white placeholder-gray-400 focus:border-indigo-400 focus:bg-white/10'
                                      : 'bg-gray-50 border border-gray-200 text-gray-800 placeholder-gray-400 focus:border-red-400 focus:bg-white focus:ring-2 focus:ring-red-100'
                                  }`}
                                />
                                {zoneSearch && (
                                  <button
                                    type="button"
                                    onClick={() => setZoneSearch('')}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-white"
                                  >
                                    <X size={14} />
                                  </button>
                                )}
                              </div>

                              {/* Region Filter Tabs */}
                              <div className="flex items-center gap-1.5 overflow-x-auto custom-scrollbar py-0.5">
                                {zoneRegionTabs.map((tab) => {
                                  const count = getRegionCount(tab.key);
                                  if (count === 0 && tab.key !== 'ALL') return null;
                                  const isActive = selectedZoneRegion === tab.key;
                                  return (
                                    <button
                                      key={tab.key}
                                      type="button"
                                      onClick={() => setSelectedZoneRegion(tab.key)}
                                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                                        isActive
                                          ? isDarkMode
                                            ? 'bg-indigo-600 text-white shadow-sm ring-1 ring-indigo-400'
                                            : 'bg-gray-900 text-white shadow-sm'
                                          : isDarkMode
                                            ? 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200/80 border border-transparent'
                                      }`}
                                    >
                                      {tab.dot && (
                                        <span className={`w-1.5 h-1.5 rounded-full ${tab.dot}`} />
                                      )}
                                      <span>{tab.label}</span>
                                      <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                                        isActive
                                          ? 'bg-white/20 text-white'
                                          : isDarkMode ? 'bg-white/10 text-gray-300' : 'bg-white text-gray-600 border border-gray-200'
                                      }`}>
                                        {count}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Zone Cards Multi-Column Grid */}
                            {filteredZoneEntries.length === 0 ? (
                              <div className="py-8 text-center">
                                <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  No zones or areas match "{zoneSearch}"
                                </p>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setZoneSearch('');
                                    setSelectedZoneRegion('ALL');
                                  }}
                                  className="mt-2 text-xs text-indigo-500 hover:underline font-semibold"
                                >
                                  Reset filters
                                </button>
                              </div>
                            ) : (
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5 max-h-[440px] overflow-y-auto custom-scrollbar pr-1">
                                {filteredZoneEntries.map(([zone, locations]) => {
                                  const regionInfo = getZoneRegionInfo(zone);
                                  return (
                                    <div
                                      key={zone}
                                      className={`rounded-xl border p-2.5 sm:p-3 flex flex-col justify-between transition-all duration-150 ${
                                        isDarkMode
                                          ? 'bg-white/[0.03] border-white/10 hover:border-white/20 hover:bg-white/[0.05]'
                                          : 'bg-white border-gray-200 hover:border-gray-300 shadow-xs hover:shadow-sm'
                                      }`}
                                    >
                                      <div>
                                        {/* Card Header */}
                                        <div className="flex items-center justify-between gap-2 mb-2 pb-1.5 border-b border-gray-100 dark:border-white/5">
                                          <div className="flex items-center gap-1.5 min-w-0">
                                            <span className={`px-2 py-0.5 rounded-md font-bold text-xs border ${getZoneColor(zone)}`}>
                                              {zone}
                                            </span>
                                            <span className={`text-[11px] font-semibold truncate ${
                                              isDarkMode ? 'text-gray-300' : 'text-gray-600'
                                            }`}>
                                              {regionInfo.region}
                                            </span>
                                          </div>
                                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold shrink-0 ${
                                            isDarkMode ? 'bg-white/5 text-gray-300' : 'bg-gray-100 text-gray-600'
                                          }`}>
                                            {locations.length} {locations.length === 1 ? 'area' : 'areas'}
                                          </span>
                                        </div>

                                        {/* Areas Badges */}
                                        <div className="flex flex-wrap gap-1">
                                          {locations.map((location, locIdx) => {
                                            const isMatched = zoneSearch.trim() && String(location).toLowerCase().includes(zoneSearch.toLowerCase().trim());
                                            return (
                                              <span
                                                key={locIdx}
                                                className={`px-2 py-0.5 rounded text-[11px] font-medium transition-colors ${
                                                  isMatched
                                                    ? isDarkMode
                                                      ? 'bg-amber-400/20 text-amber-200 border border-amber-400/40 ring-1 ring-amber-400/30 font-semibold'
                                                      : 'bg-amber-100 text-amber-900 border border-amber-300 ring-1 ring-amber-400/30 font-semibold'
                                                    : isDarkMode
                                                      ? 'bg-white/5 text-gray-300 border border-white/10 hover:bg-white/10'
                                                      : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
                                                }`}
                                              >
                                                {location}
                                              </span>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {/* ===== CONTENT FOR ACTIVE PARTNER ===== */}
                {(() => {
                  const activeItem = (data || []).find((item, idx) => {
                    if (selectedCardId !== null) {
                      return item.id !== undefined && item.id !== null ? item.id === selectedCardId : idx === selectedCardId;
                    }
                    return item.partner === activePartner;
                  }) || data[0];
                  if (!activeItem) return null;
                  const pricingData = parsePricingData(activeItem.zone_pricing);
                  const vasCharges = parseVasData(activeItem.vas_pricing);
                  const activeCharges = Array.isArray(vasCharges)
                    ? vasCharges.filter(charge => charge && Boolean(charge.is_active))
                    : [];
                  const zones = Object.keys(pricingData);
                  const activeCft = activeItem.cft_type || activeItem.type || activeItem.cft || (data.indexOf(activeItem) === 0 ? '6' : data.indexOf(activeItem) === 1 ? '8' : '10');

                  return (
                    <motion.div
                      key={activeItem.id || activeItem.partner || selectedCardId}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className={`${isDarkMode ? 'dark:bg-darkmodeColor border-indigo-500/25' : 'bg-white border-gray-200'} rounded-2xl overflow-hidden border`}
                    >
                      {/* Partner Header */}
                      <div className={`px-4 sm:px-5 py-4 flex items-center justify-between gap-3 border-b ${isDarkMode ? 'bg-indigo-500/5 border-indigo-500/25' : 'bg-gradient-to-r from-gray-50 via-white to-gray-50 border-gray-100'}`}>
                        <div className="flex items-center gap-3.5 min-w-0">
                          <div className={`p-2.5 rounded-2xl flex-shrink-0 ${isDarkMode ? 'bg-white/5 border border-indigo-500/30' : 'bg-white border border-gray-200 shadow-sm'}`}>
                            <img
                              src={getPartnerLogo(activeItem.partner)}
                              alt={`${activeItem.partner} logo`}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/assets/images/logo1.png';
                              }}
                              className="w-12 h-12 object-contain"
                            />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h2 className={`text-lg sm:text-xl font-bold capitalize truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {activeItem.partner}
                              </h2>
                              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30">
                                CFT: {activeCft}
                              </span>
                              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30">
                                Active Rates
                              </span>
                            </div>
                            <div className={`inline-flex items-center gap-2 text-xs mt-1 font-medium ${isDarkMode ? 'text-indigo-300/70' : 'text-gray-500'}`}>
                              <span className="inline-flex items-center gap-1">
                                <Layers size={13} className="text-blue-500" />
                                {zones.length} zones available
                              </span>
                              <span>•</span>
                              <span>{activeCharges.length} VAS charges</span>
                              <span>•</span>
                              <span>Surface B2B Cargo</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-3 sm:p-4 space-y-4">

                        {/* ===== VAS CHARGES SECTION ===== */}
                        {activeCharges.length > 0 && (() => {
                          const partnerKey = activeItem.id || activeItem.partner;
                          const filteredCharges = activeCharges.filter(charge => {
                            if (!charge) return false;
                            const typeMatches = vasTypeFilter === 'ALL' || String(charge.charge_type || '').toLowerCase() === vasTypeFilter.toLowerCase();
                            if (!typeMatches) return false;
                            if (!vasSearch.trim()) return true;
                            const q = vasSearch.toLowerCase().trim();
                            const labelMatches = String(charge.label || '').toLowerCase().includes(q);
                            const keyMatches = String(charge.charge_key || '').toLowerCase().includes(q);
                            return labelMatches || keyMatches;
                          });

                          return (
                            <div className={`rounded-2xl overflow-hidden border transition-all duration-200 ${
                              isDarkMode ? 'border-indigo-500/25 bg-darkmodeColor shadow-lg shadow-black/20' : 'border-gray-200 bg-white shadow-sm'
                            }`}>
                              {/* Accordion Header */}
                              <button
                                type="button"
                                onClick={() => toggleVasCharges(partnerKey)}
                                className={`w-full px-4 py-3 sm:py-3.5 flex items-center justify-between gap-3 text-left transition-all ${
                                  isDarkMode
                                    ? 'bg-indigo-500/10 text-white hover:bg-indigo-500/15'
                                    : 'bg-gradient-to-r from-gray-50 via-white to-gray-50 text-gray-900 hover:bg-gray-100/60'
                                }`}
                              >
                                <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                    isDarkMode ? 'bg-indigo-500/20 text-indigo-300' : 'bg-red-50 text-red-600 border border-red-100'
                                  }`}>
                                    <Receipt size={16} />
                                  </div>
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="font-bold text-sm sm:text-base">
                                        Value Added Services (VAS Charges)
                                      </span>
                                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                                        isDarkMode ? 'bg-indigo-500/30 text-indigo-200 border border-indigo-400/30' : 'bg-red-50 text-red-700 border border-red-200'
                                      }`}>
                                        {activeCharges.length} Charges
                                      </span>
                                    </div>
                                    <p className={`text-[11px] truncate mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                      Applicable surcharges for handling, demurrage, ODA and specialized delivery
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span className={`text-xs font-semibold hidden md:inline ${
                                    isDarkMode ? 'text-indigo-300/70' : 'text-gray-500'
                                  }`}>
                                    {showVasCharges[partnerKey] ? 'Hide Charges' : 'View Charges'}
                                  </span>
                                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                                    isDarkMode ? 'bg-white/5 text-indigo-300' : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    <ChevronDown
                                      className={`w-4 h-4 transition-transform duration-300 ${
                                        showVasCharges[partnerKey] ? 'rotate-180' : ''
                                      }`}
                                    />
                                  </div>
                                </div>
                              </button>

                              {/* Accordion Content */}
                              <AnimatePresence initial={false}>
                                {showVasCharges[partnerKey] && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.25 }}
                                    className={`overflow-hidden border-t ${
                                      isDarkMode ? 'dark:bg-darkmodeColor border-indigo-500/20' : 'bg-white border-gray-100'
                                    }`}
                                  >
                                    <div className="p-3 sm:p-4 space-y-3">
                                      {/* Search & Type Filters */}
                                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 pb-2 border-b border-gray-100 dark:border-white/5">
                                        {/* Search Input */}
                                        <div className="relative flex-1 max-w-sm">
                                          <Search
                                            size={15}
                                            className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                                              isDarkMode ? 'text-gray-400' : 'text-gray-400'
                                            }`}
                                          />
                                          <input
                                            type="text"
                                            value={vasSearch}
                                            onChange={(e) => setVasSearch(e.target.value)}
                                            placeholder="Search charge (e.g. ODA, Demurrage)..."
                                            className={`w-full pl-9 pr-8 py-1.5 rounded-lg text-xs outline-none transition-all ${
                                              isDarkMode
                                                ? 'bg-white/5 border border-white/10 text-white placeholder-gray-400 focus:border-indigo-400 focus:bg-white/10'
                                                : 'bg-gray-50 border border-gray-200 text-gray-800 placeholder-gray-400 focus:border-red-400 focus:bg-white focus:ring-2 focus:ring-red-100'
                                            }`}
                                          />
                                          {vasSearch && (
                                            <button
                                              type="button"
                                              onClick={() => setVasSearch('')}
                                              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-white"
                                            >
                                              <X size={14} />
                                            </button>
                                          )}
                                        </div>

                                        {/* Type Filter Pills */}
                                        <div className="flex items-center gap-1.5 overflow-x-auto custom-scrollbar py-0.5">
                                          {[
                                            { key: 'ALL', label: 'All' },
                                            { key: 'fixed', label: 'Fixed Fee' },
                                            { key: 'per_kg', label: 'Per Kg' },
                                            { key: 'percent', label: 'Percentage' },
                                          ].map(pill => {
                                            const isActive = vasTypeFilter === pill.key;
                                            return (
                                              <button
                                                key={pill.key}
                                                type="button"
                                                onClick={() => setVasTypeFilter(pill.key)}
                                                className={`px-2.5 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                                                  isActive
                                                    ? isDarkMode
                                                      ? 'bg-indigo-600 text-white shadow-sm ring-1 ring-indigo-400'
                                                      : 'bg-gray-900 text-white shadow-sm'
                                                    : isDarkMode
                                                      ? 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10'
                                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200/80 border border-transparent'
                                                }`}
                                              >
                                                {pill.label}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      </div>

                                      {/* Charges Cards Grid */}
                                      {filteredCharges.length === 0 ? (
                                        <div className="py-8 text-center">
                                          <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            No charges match "{vasSearch}"
                                          </p>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setVasSearch('');
                                              setVasTypeFilter('ALL');
                                            }}
                                            className="mt-2 text-xs text-indigo-500 hover:underline font-semibold"
                                          >
                                            Reset filter
                                          </button>
                                        </div>
                                      ) : (
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5 max-h-[440px] overflow-y-auto custom-scrollbar pr-1">
                                          {filteredCharges.map((charge, idx) => {
                                            const condText = formatConditionValue(charge.condition_value);
                                            const hasLimits = Boolean(charge.min_value || charge.max_value);

                                            return (
                                              <div
                                                key={charge.charge_key || idx}
                                                className={`group rounded-xl border p-3 flex flex-col justify-between transition-all duration-150 hover:-translate-y-0.5 ${
                                                  isDarkMode
                                                    ? 'bg-white/[0.03] border-white/10 hover:border-indigo-400/50 hover:bg-white/[0.05] hover:shadow-lg hover:shadow-indigo-950/20'
                                                    : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm shadow-xs'
                                                }`}
                                              >
                                                <div>
                                                  {/* Top Header: Icon + Title + Price Badge */}
                                                  <div className="flex items-start justify-between gap-2.5 mb-2">
                                                    <div className="flex items-center gap-2.5 min-w-0">
                                                      <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 border ${
                                                        isDarkMode ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'
                                                      }`}>
                                                        {getVasIcon(charge.charge_key, charge.label)}
                                                      </div>
                                                      <div className="min-w-0">
                                                        <h4 className={`text-xs sm:text-sm font-bold truncate leading-tight ${
                                                          isDarkMode ? 'text-white' : 'text-gray-900'
                                                        }`}>
                                                          {formatChargeLabel(charge)}
                                                        </h4>
                                                        {charge.charge_key && (
                                                          <span className={`text-[10px] font-semibold tracking-wider uppercase block truncate ${
                                                            isDarkMode ? 'text-gray-400' : 'text-gray-400'
                                                          }`}>
                                                            {charge.charge_key}
                                                          </span>
                                                        )}
                                                      </div>
                                                    </div>

                                                    {/* Price Pill */}
                                                    <div className="shrink-0">
                                                      <span className={`inline-flex items-center px-2.5 py-1 rounded-lg font-extrabold text-xs sm:text-sm shadow-xs ${
                                                        isDarkMode
                                                          ? 'bg-indigo-500/20 text-indigo-200 border border-indigo-400/30'
                                                          : 'bg-red-50 text-red-700 border border-red-200'
                                                      }`}>
                                                        {formatChargeValue(charge)}
                                                      </span>
                                                    </div>
                                                  </div>

                                                  {/* Middle Row: Calculation Method & Basis Badges */}
                                                  <div className="flex flex-wrap items-center gap-1.5 my-1.5">
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                                      isDarkMode
                                                        ? 'bg-purple-500/15 text-purple-200 border border-purple-500/30'
                                                        : 'bg-purple-50 text-purple-700 border border-purple-200'
                                                    }`}>
                                                      {formatChargeType(charge.charge_type)}
                                                    </span>

                                                    {condText && (
                                                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                                                        isDarkMode
                                                          ? 'bg-white/5 text-gray-300 border border-white/10'
                                                          : 'bg-gray-100 text-gray-600 border border-gray-200'
                                                      }`}>
                                                        {condText}
                                                      </span>
                                                    )}
                                                  </div>
                                                </div>

                                                {/* Footer: Min / Max Limits */}
                                                {hasLimits && (
                                                  <div className={`mt-2 pt-1.5 border-t flex items-center justify-between text-[10px] ${
                                                    isDarkMode ? 'border-white/5 text-gray-400' : 'border-gray-100 text-gray-500'
                                                  }`}>
                                                    {charge.min_value ? (
                                                      <span className="flex items-center gap-1">
                                                        <span className="font-bold text-amber-600 dark:text-amber-400">Min:</span>
                                                        <span>₹{charge.min_value}</span>
                                                      </span>
                                                    ) : <span />}

                                                    {charge.max_value ? (
                                                      <span className="flex items-center gap-1">
                                                        <span className="font-bold text-amber-600 dark:text-amber-400">Max:</span>
                                                        <span>₹{charge.max_value}</span>
                                                      </span>
                                                    ) : <span />}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          );
                        })()}

                        {/* ===== ZONE PRICING SECTION ===== */}
                        <div className={`rounded-2xl overflow-hidden border transition-all duration-200 ${
                          isDarkMode ? 'border-indigo-500/25 bg-darkmodeColor shadow-lg shadow-black/20' : 'border-gray-200 bg-white shadow-sm'
                        }`}>
                          {/* Header */}
                          <div className={`px-4 sm:px-5 py-3.5 flex flex-col md:flex-row md:items-center justify-between gap-3 border-b ${
                            isDarkMode ? 'bg-indigo-500/10 border-indigo-500/25' : 'bg-gradient-to-r from-gray-50 via-white to-gray-50 border-gray-100'
                          }`}>
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                isDarkMode ? 'bg-indigo-500/20 text-indigo-300' : 'bg-red-50 text-red-600 border border-red-100'
                              }`}>
                                <IndianRupee size={16} />
                              </div>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className={`font-bold text-sm sm:text-base ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                    Zone Pricing Matrix
                                  </h3>
                                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                                    isDarkMode ? 'bg-indigo-500/30 text-indigo-200 border border-indigo-400/30' : 'bg-red-50 text-red-700 border border-red-200'
                                  }`}>
                                    CFT: {activeCft}
                                  </span>
                                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                                    isDarkMode ? 'bg-white/5 text-gray-300' : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    Per-Kg Surface Cargo Rates
                                  </span>
                                </div>
                                <p className={`text-[11px] truncate mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  Matrix of origin to destination per-kg rates across all 16 logistics zones
                                </p>
                              </div>
                            </div>

                            {/* Origin Filter Tabs */}
                            <div className="flex items-center gap-1 overflow-x-auto custom-scrollbar py-0.5">
                              {zoneRegionTabs.map((tab) => {
                                const isActive = matrixOriginRegion === tab.key;
                                return (
                                  <button
                                    key={tab.key}
                                    type="button"
                                    onClick={() => setMatrixOriginRegion(tab.key)}
                                    className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                                      isActive
                                        ? isDarkMode
                                          ? 'bg-indigo-600 text-white shadow-sm ring-1 ring-indigo-400'
                                          : 'bg-gray-900 text-white shadow-sm'
                                        : isDarkMode
                                          ? 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10'
                                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent'
                                    }`}
                                  >
                                    {tab.dot && <span className={`w-1.5 h-1.5 rounded-full ${tab.dot}`} />}
                                    <span>{tab.label.replace('All Regions', 'All Origins')}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {/* Quick Interactive Rate Calculator Bar */}
                          <div className={`px-4 py-3 border-b flex flex-wrap items-center justify-between gap-2.5 ${
                            isDarkMode ? 'bg-white/[0.02] border-white/5' : 'bg-gray-50/70 border-gray-100'
                          }`}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-xs font-bold uppercase tracking-wider ${
                                isDarkMode ? 'text-indigo-300/80' : 'text-gray-600'
                              }`}>
                                Quick Route Lookup:
                              </span>

                              {/* Origin selector */}
                              <div className="flex items-center gap-1.5">
                                <span className={`text-[11px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>From</span>
                                <select
                                  value={matrixOrigin}
                                  onChange={(e) => setMatrixOrigin(e.target.value)}
                                  className={`text-xs font-bold py-1 px-2 rounded-lg border outline-none transition-all ${
                                    isDarkMode
                                      ? 'bg-darkmodeColor border-white/10 text-white focus:border-indigo-400'
                                      : 'bg-white border-gray-200 text-gray-800 focus:border-red-400 focus:ring-1 focus:ring-red-200'
                                  }`}
                                >
                                  <option value="">Choose Origin</option>
                                  {zones.map(z => {
                                    const mapped = zoneMapping[z] || z.toUpperCase();
                                    return (
                                      <option key={z} value={z}>
                                        {mapped} ({getZoneRegionInfo(mapped).region})
                                      </option>
                                    );
                                  })}
                                </select>
                              </div>

                              <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>➔</span>

                              {/* Destination selector */}
                              <div className="flex items-center gap-1.5">
                                <span className={`text-[11px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>To</span>
                                <select
                                  value={matrixDest}
                                  onChange={(e) => setMatrixDest(e.target.value)}
                                  className={`text-xs font-bold py-1 px-2 rounded-lg border outline-none transition-all ${
                                    isDarkMode
                                      ? 'bg-darkmodeColor border-white/10 text-white focus:border-indigo-400'
                                      : 'bg-white border-gray-200 text-gray-800 focus:border-red-400 focus:ring-1 focus:ring-red-200'
                                  }`}
                                >
                                  <option value="">Choose Destination</option>
                                  {zones.map(z => {
                                    const mapped = zoneMapping[z] || z.toUpperCase();
                                    return (
                                      <option key={z} value={z}>
                                        {mapped} ({getZoneRegionInfo(mapped).region})
                                      </option>
                                    );
                                  })}
                                </select>
                              </div>

                              {(matrixOrigin || matrixDest) && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setMatrixOrigin('');
                                    setMatrixDest('');
                                  }}
                                  className="text-[11px] text-gray-400 hover:text-gray-600 dark:hover:text-white underline ml-1"
                                >
                                  Reset
                                </button>
                              )}
                            </div>

                            {/* Lookup Result Pill */}
                            {matrixOrigin && matrixDest && (
                              <div className={`px-3 py-1 rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm ${
                                isDarkMode
                                  ? 'bg-indigo-600 text-white shadow-indigo-950/40 ring-1 ring-indigo-400'
                                  : 'bg-red-500 text-white shadow-red-200'
                              }`}>
                                <span>{(zoneMapping[matrixOrigin] || matrixOrigin.toUpperCase())} ➔ {(zoneMapping[matrixDest] || matrixDest.toUpperCase())}:</span>
                                <span className="text-sm font-extrabold">
                                  ₹{getZonePricing(pricingData, matrixOrigin, matrixDest)}
                                </span>
                                <span className="text-[10px] opacity-80">/kg</span>
                              </div>
                            )}
                          </div>

                          {/* ===== DESKTOP TABLE (hidden below md) ===== */}
                          <div className="hidden md:block overflow-x-auto custom-scrollbar">
                            <table className="min-w-full border-collapse">
                              <thead>
                                <tr className={`border-b ${isDarkMode ? 'bg-white/[0.02] border-white/10' : 'bg-gray-50/80 border-gray-200'}`}>
                                  <th className={`px-4 py-3 text-left text-[11px] uppercase tracking-wider font-bold ${
                                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                                  }`}>
                                    Origin Zone
                                  </th>
                                  {[
                                    { title: 'North', subtitle: 'N1 - N4', color: 'text-blue-700 dark:text-blue-300', bg: 'bg-blue-50 dark:bg-blue-500/15', border: 'border-blue-200 dark:border-blue-500/30' },
                                    { title: 'Central', subtitle: 'C1 - C2', color: 'text-violet-700 dark:text-violet-300', bg: 'bg-violet-50 dark:bg-violet-500/15', border: 'border-violet-200 dark:border-violet-500/30' },
                                    { title: 'West', subtitle: 'W1 - W2', color: 'text-pink-700 dark:text-pink-300', bg: 'bg-pink-50 dark:bg-pink-500/15', border: 'border-pink-200 dark:border-pink-500/30' },
                                    { title: 'South', subtitle: 'S1 - S4', color: 'text-orange-700 dark:text-orange-300', bg: 'bg-orange-50 dark:bg-orange-500/15', border: 'border-orange-200 dark:border-orange-500/30' },
                                    { title: 'East', subtitle: 'E1 - E2', color: 'text-emerald-700 dark:text-emerald-300', bg: 'bg-emerald-50 dark:bg-emerald-500/15', border: 'border-emerald-200 dark:border-emerald-500/30' },
                                    { title: 'North-East', subtitle: 'NE1 - NE2', color: 'text-teal-700 dark:text-teal-300', bg: 'bg-teal-50 dark:bg-teal-500/15', border: 'border-teal-200 dark:border-teal-500/30' },
                                  ].map(col => (
                                    <th key={col.title} className="px-3 py-2.5 text-left">
                                      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border ${col.bg} ${col.border}`}>
                                        <span className={`text-xs font-bold ${col.color}`}>{col.title}</span>
                                        <span className={`text-[10px] font-semibold opacity-75 ${col.color}`}>({col.subtitle})</span>
                                      </div>
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {zones
                                  .filter(z => {
                                    if (matrixOriginRegion === 'ALL') return true;
                                    const mapped = zoneMapping[z] || z.toUpperCase();
                                    return getZoneRegionInfo(mapped).key === matrixOriginRegion;
                                  })
                                  .map((zone, zoneIndex) => {
                                    const mappedZone = zoneMapping[zone] || zone.toUpperCase();
                                    const isOriginSelected = matrixOrigin && matrixOrigin.toLowerCase() === zone.toLowerCase();

                                    return (
                                      <tr
                                        key={zone}
                                        className={`transition-colors duration-150 border-b ${
                                          isDarkMode ? 'border-white/5' : 'border-gray-100'
                                        } ${
                                          isOriginSelected
                                            ? isDarkMode ? 'bg-indigo-600/20 ring-1 ring-inset ring-indigo-400' : 'bg-red-50/70 ring-1 ring-inset ring-red-300'
                                            : zoneIndex % 2 === 0
                                              ? isDarkMode ? 'bg-white/[0.01]' : 'bg-white'
                                              : isDarkMode ? 'bg-white/[0.02]' : 'bg-gray-50/50'
                                        } hover:bg-indigo-50/40 dark:hover:bg-indigo-500/10`}
                                      >
                                        {/* Origin Zone Header */}
                                        <td className="px-4 py-2.5">
                                          <div className="flex items-center gap-2">
                                            <span className={`font-bold text-xs px-2.5 py-1 rounded-lg border shadow-xs ${getZoneColor(mappedZone)}`}>
                                              {mappedZone}
                                            </span>
                                            <span className={`text-[11px] font-medium hidden lg:inline ${
                                              isDarkMode ? 'text-gray-400' : 'text-gray-500'
                                            }`}>
                                              {getZoneRegionInfo(mappedZone).region}
                                            </span>
                                          </div>
                                        </td>

                                        {/* Region Destination Cells */}
                                        {[
                                          { key: 'north', cols: 2, zones: ['n1', 'n2', 'n3', 'n4'] },
                                          { key: 'central', cols: 1, zones: ['c1', 'c2'] },
                                          { key: 'west', cols: 1, zones: ['w1', 'w2'] },
                                          { key: 'south', cols: 2, zones: ['s1', 's2', 's3', 's4'] },
                                          { key: 'east', cols: 1, zones: ['e1', 'e2'] },
                                          { key: 'northeast', cols: 1, zones: ['ne1', 'ne2'] },
                                        ].map(group => (
                                          <td key={group.key} className="px-3 py-2">
                                            <div className={`grid ${group.cols === 2 ? 'grid-cols-2' : 'grid-cols-1'} gap-1.5`}>
                                              {group.zones.map(z => {
                                                const isDestSelected = matrixDest && matrixDest.toLowerCase() === z.toLowerCase();
                                                const isExactMatch = isOriginSelected && isDestSelected;

                                                return (
                                                  <div
                                                    key={z}
                                                    className={`flex items-center justify-between px-2 py-1 rounded-lg border transition-all ${
                                                      isExactMatch
                                                        ? isDarkMode
                                                          ? 'bg-indigo-600 text-white border-indigo-400 shadow-md ring-2 ring-indigo-400/50'
                                                          : 'bg-red-500 text-white border-red-600 shadow-md ring-2 ring-red-400/40'
                                                        : isDestSelected
                                                          ? isDarkMode
                                                            ? 'bg-indigo-500/25 border-indigo-400/60 text-indigo-100 ring-1 ring-indigo-400/30'
                                                            : 'bg-red-50 border-red-300 text-red-900 ring-1 ring-red-400/30'
                                                          : isDarkMode
                                                            ? 'bg-white/[0.03] border-white/5 hover:bg-white/[0.08] hover:border-white/10'
                                                            : 'bg-white border-gray-200/80 hover:bg-gray-50 hover:border-gray-300 shadow-2xs'
                                                    }`}
                                                  >
                                                    <span className={`text-[10px] font-bold ${
                                                      isExactMatch
                                                        ? 'text-white'
                                                        : isDestSelected
                                                          ? isDarkMode ? 'text-indigo-200' : 'text-red-700'
                                                          : isDarkMode ? 'text-gray-400' : 'text-gray-400'
                                                    }`}>
                                                      {z.toUpperCase()}
                                                    </span>
                                                    <span className={`text-xs font-bold ${
                                                      isExactMatch
                                                        ? 'text-white font-extrabold'
                                                        : isDestSelected
                                                          ? isDarkMode ? 'text-indigo-100 font-extrabold' : 'text-red-600 font-extrabold'
                                                          : isDarkMode ? 'text-gray-100' : 'text-gray-800'
                                                    }`}>
                                                      ₹{getZonePricing(pricingData, zone, z)}
                                                    </span>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </td>
                                        ))}
                                      </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>

                          {/* ===== MOBILE CARD VIEW (shown below md) ===== */}
                          <div className="md:hidden p-3 space-y-3">
                            {zones
                              .filter(z => {
                                if (matrixOriginRegion === 'ALL') return true;
                                const mapped = zoneMapping[z] || z.toUpperCase();
                                return getZoneRegionInfo(mapped).key === matrixOriginRegion;
                              })
                              .map((zone) => {
                                const mappedZone = zoneMapping[zone] || zone.toUpperCase();
                                return (
                                  <div
                                    key={zone}
                                    className={`rounded-xl border overflow-hidden ${
                                      isDarkMode ? 'border-indigo-500/25 bg-white/[0.02]' : 'border-gray-200 bg-white shadow-xs'
                                    }`}
                                  >
                                    {/* Zone header */}
                                    <div className={`px-3 py-2.5 flex items-center justify-between border-b ${
                                      isDarkMode ? 'bg-indigo-500/10 border-indigo-500/20' : 'bg-gray-50 border-gray-100'
                                    }`}>
                                      <div className="flex items-center gap-2">
                                        <span className={`font-bold text-xs px-2.5 py-0.5 rounded-lg border ${getZoneColor(mappedZone)}`}>
                                          {mappedZone}
                                        </span>
                                        <span className={`text-xs font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                          {getZoneRegionInfo(mappedZone).region}
                                        </span>
                                      </div>
                                      <span className={`text-[10px] font-semibold uppercase tracking-wider ${isDarkMode ? 'text-indigo-300/60' : 'text-gray-400'}`}>
                                        Origin Zone
                                      </span>
                                    </div>

                                    {/* Region destination rows */}
                                    <div className="p-2 space-y-2">
                                      {regionGroups.map((region) => (
                                        <div key={region.title} className={`rounded-lg p-2 border ${
                                          isDarkMode ? 'bg-white/[0.02] border-white/5' : 'bg-gray-50/60 border-gray-100'
                                        }`}>
                                          <p className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${
                                            isDarkMode ? 'text-indigo-300/80' : 'text-gray-600'
                                          }`}>
                                            To {region.title}
                                          </p>
                                          <div className="grid grid-cols-2 gap-1.5">
                                            {region.zones.map(z => (
                                              <div
                                                key={z}
                                                className={`flex items-center justify-between px-2 py-1 rounded border ${
                                                  isDarkMode ? 'bg-white/5 border-white/5' : 'bg-white border-gray-200'
                                                }`}
                                              >
                                                <span className={`text-[10px] font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
                                                  {z.toUpperCase()}
                                                </span>
                                                <span className={`text-xs font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                                                  ₹{getZonePricing(pricingData, zone, z)}
                                                </span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })()}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default B2BRateCard;